import React, { Fragment, useEffect, useState } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { Settings, AllInbox, AssignmentTurnedIn } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { State } from '../../components/_EOS/State'
import {
  CustomActionProps,
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useApi } from '../../hooks/useApi'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { useSnackbar } from '../../hooks/useSnackbar'
import { ApiResponse } from '../../typings/ApiResponse'
import { useProductOrigins } from '../../hooks'
import { WorkOrderInBoundGrid } from '../../interfaces/WorkOrder/WorkOrder'
import { ASNExcessModal } from './modals/ASNExcessModal'
import { ASNReleaseModal } from './modals/ASNReleaseModal'
import { getHeaderConfigHeaders } from './config/headers'
import { GetInboundAdjust } from './config/api'
import { ASNAdjustmentSearch } from './search'
import { Filter } from '../../components/_WMS/FilterComponent'
import { defaultFilters } from './search/config'
import { ASNAdjustmentModalNew } from './modals/ASNAdjustmentModalNew'

export const ASNAdjustmentPage: React.FC = () => {
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)
  const { state: asnApiState, callApi: requestGetAsn } =
    useApi<ApiResponse<WorkOrderInBoundGrid>>()

  const [productOriginId] = useState<number | undefined>(undefined)

  // State
  const [selectedFilters, setSelectedFilters] =
    useState<Filter[]>(defaultFilters)
  const [orderBy, setOrderBy] = useState<GenericOrderBy<WorkOrderInBoundGrid>>({
    field: 'receivedDate',
    direction: 'desc'
  })

  // Data fetching
  const fetchAsnData = (
    page: number,
    pageSize: number,
    filters: Filter[],
    order: GenericOrderBy<WorkOrderInBoundGrid>,
    productOriginId?: number
  ) => {
    requestGetAsn(
      GetInboundAdjust(page, pageSize, order, productOriginId, filters)
    ).catch((error) =>
      openSnackBar(`Error al cargar datos: ${error.message}`, false)
    )
  }

  const asnAdjustmentModal = useModal<{
    workOrderId: number
    asnNumber: string
  }>('Ajustar ASN')

  const excessModalState = useModal<{ workOrderId: number }>(
    'Declarar Sobrantes'
  )
  const releaseModalState = useModal<{
    workOrderId: number
    asnId: number
    asnNumber: string
  }>('')

  const {
    // productOriginsState,
    getProductOrigins
  } = useProductOrigins()

  // Effects
  useEffect(() => {
    fetchAsnData(
      0,
      pagination.pageSize,
      selectedFilters,
      orderBy,
      productOriginId
    )
    pagination.setPagination(0, pagination.pageSize)
  }, [orderBy, selectedFilters])

  useEffect(() => {
    getProductOrigins()
  }, [])

  // Handlers
  const handlePagination = (page: number, pageSize: number) => {
    fetchAsnData(page, pageSize, selectedFilters, orderBy)
    pagination.setPagination(page, pageSize)
  }

  const handleSorting = ({ field }: GenericOrderBy<WorkOrderInBoundGrid>) => {
    const newDirection =
      field === orderBy.field ? getNewDirection(orderBy.direction) : 'asc'
    setOrderBy({ field, direction: newDirection })
  }

  const AsnCustomActions: React.FC<CustomActionProps<WorkOrderInBoundGrid>> = ({
    row
  }) => {
    const isDisabled = row.differenceQty > 0
    return (
      <Fragment>
        <Tooltip title='Ajustar' aria-label='adjust'>
          <span>
            <IconButton
              onClick={() => {
                asnAdjustmentModal.openModal('lg', {
                  workOrderId: row.workOrderId,
                  asnNumber: row.asnNumber
                })
              }}
            >
              <Settings />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title='Sobrantes' aria-label='overage'>
          <span>
            <IconButton
              disabled={row.differenceQty === 0}
              onClick={() =>
                excessModalState.openModal('lg', {
                  workOrderId: row.workOrderId
                })
              }
            >
              <AllInbox />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Finalizar' aria-label='release'>
          <span>
            <IconButton
              disabled={isDisabled}
              onClick={() =>
                releaseModalState.openModal('lg', {
                  workOrderId: row.workOrderId,
                  asnId: row.asnId,
                  asnNumber: row.asnNumber
                })
              }
            >
              <AssignmentTurnedIn />
            </IconButton>
          </span>
        </Tooltip>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <StandardPage pageTitle={'Ajustar ASN'}>
        <ASNAdjustmentSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={() =>
            fetchAsnData(0, pagination.pageSize, selectedFilters, orderBy)
          }
        />
        <State state={asnApiState} loadingMessage='Obteniendo ASNs...'>
          <GenericTable<WorkOrderInBoundGrid>
            data={asnApiState.data?.items}
            headersConfig={getHeaderConfigHeaders()}
            IDKey={'workOrderId'}
            customActionComponent={AsnCustomActions}
            pagination={{
              page: pagination.page,
              pageSize: pagination.pageSize,
              totalItem: asnApiState.data?.totalItems || 0
            }}
            onPaginationChange={handlePagination}
            onSortChange={handleSorting}
            orderField={orderBy}
          />
        </State>
      </StandardPage>

      {/* ************ */}
      {/*    MODALS    */}
      {/* ************ */}
      <ASNAdjustmentModalNew
        asnAdjustmentModalState={asnAdjustmentModal}
        handleSnackbarNotification={openSnackBar}
        refetchGridData={() =>
          fetchAsnData(
            pagination.page,
            pagination.pageSize,
            selectedFilters,
            orderBy
          )
        }
      />

      <ASNExcessModal
        excessModalState={excessModalState}
        handleSnackbar={openSnackBar}
      />

      <ASNReleaseModal
        asnAdjustmentModalState={releaseModalState}
        open={releaseModalState.isOpen}
        onClose={releaseModalState.closeModal}
        handleSnackbar={openSnackBar}
        refetchGridData={() =>
          fetchAsnData(
            pagination.page,
            pagination.pageSize,
            selectedFilters,
            orderBy
          )
        }
      />

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
